import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Modal as AntdModal } from 'antd'
import cl from 'classnames'
import { observer } from 'mobx-react'

import { Icon } from 'Shared/atoms'
import useBasicStores from 'Shared/hooks/useBasicStores'

import DiscardChangesModal from './DiscardChangesModal'
import { WarningModalState } from './types'

import styles from './styles.module.scss'

export const FORM_MODAL_WIDTH = { //TODO: correct MEDIUM vs LARGE values
	XXXXLARGE: 1232,
	XXXLARGE: 1062,
	XXLARGE: 846,
	XLARGE: 720,
	LARGE: 510,
	MEDIUM: 628,
	SMALL: 410,
	SMALL2: 430,
	SMALL3: 450,
	XSMALL: 320
}

interface ModalInterface extends React.FC {
	ModalBody: React.ComponentType<React.PropsWithChildren<ModalBodyProps>>
	ModalFooter: React.ComponentType<React.PropsWithChildren<ModalFooterProps>>
}

interface ModalBodyProps {
	hasPadding?: boolean
	className?: string
	isMutationOverflow?: boolean
	moodalRef?: React.MutableRefObject<HTMLDivElement | null>
}

interface ModalFooterProps {
	className?: string
}

const ModalBody = ({
	hasPadding = true,
	className,
	children,
	moodalRef
}: React.PropsWithChildren<ModalBodyProps>) => {
	return (
		<div
			ref={moodalRef}
			className={cl(className, styles.modalBody, { [styles.modalBodyPadding]: hasPadding })}
		>
			{children}
		</div>
	)
}

const ModalFooter = ({
	children,
	className
}: React.PropsWithChildren<ModalFooterProps>) => {
	return <div className={cl(className, styles.modalFooter)}>{children}</div>
}

const Modal: ModalInterface = () => {
	const {
		uiStore: {
			isModalOpened,
			closeModal,
			modalStack,
			updateModalTitle,
			updateModalClosable,
			updateModalWidth
		}
	} = useBasicStores()

	const [warningModalState, setWarningModalState] = useState<WarningModalState>({
		isOpened: false,
		originalProps: null
	})

	const history = useHistory()

	// Get the topmost modal for display properties
	const topModalProps = modalStack.length > 0 ? modalStack[modalStack.length - 1] : null

	const handleCloseWarningModal = () => {
		if (warningModalState.originalProps) {
			const { title, width, closable } = warningModalState.originalProps

			title && updateModalTitle(title)
			width && updateModalWidth(width)
			closable && updateModalClosable(closable)
		}

		setWarningModalState({
			isOpened: false,
			originalProps: null
		})
	}

	const handleOpenWarningModal = () => {
		setWarningModalState({
			isOpened: true,
			originalProps: {
				title: topModalProps?.title,
				width: topModalProps?.width,
				closable: topModalProps?.closable || true
			}
		})

		updateModalTitle()
		updateModalWidth(FORM_MODAL_WIDTH.SMALL)
		updateModalClosable(false)
	}

	useEffect(() => {
		const unlisten = history.listen(() => {
			if (history.action === 'POP' && isModalOpened) {
				closeModal()
			}
		})
		return unlisten
	}, [closeModal, history, isModalOpened])

	if (!topModalProps?.component || !isModalOpened) {
		return null
	}

	const {
		title,
		width = FORM_MODAL_WIDTH.SMALL,
		closable,
		className,
		showWarningOnClose
	} = topModalProps

	const onCloseModal = showWarningOnClose ? handleOpenWarningModal : closeModal

	return (
		<AntdModal
			title={title}
			open={isModalOpened}
			onCancel={onCloseModal}
			width={width}
			closable={closable}
			centered
			wrapClassName={styles.modalLayout}
			className={cl(styles.modal, className)}
			footer={null}
			maskClosable={false}
			closeIcon={<Icon type="close" />}
			destroyOnClose={false} // Keep modal content alive to preserve form state
		>
			{/* Render all modal contents, but only show the topmost one */}
			{modalStack.map((modalProps, index) => {
				const ModalContent = modalProps.component
				const isTopModal = index === modalStack.length - 1

				return (
					<div
						key={index}
						className={cl(styles.modalContent, {
							[styles.hiddenModal]: !isTopModal || warningModalState.isOpened
						})}
						style={{ display: isTopModal ? 'block' : 'none' }}
					>
						<ModalContent {...modalProps} closeModal={onCloseModal} />
					</div>
				)
			})}
			{warningModalState.isOpened && (
				<DiscardChangesModal
					handleContinue={handleCloseWarningModal}
					handleDiscard={() => {
						setWarningModalState({
							isOpened: false,
							originalProps: null
						})
						closeModal()
					}}
				/>
			)}
		</AntdModal>
	)
}

Modal.ModalBody = ModalBody
Modal.ModalFooter = ModalFooter

export default observer(Modal)
